import React, { useEffect, useState } from "react";
import {
  Typography,
  Layout,
  Button,
  Table,
  Skeleton,
  Modal,
  Form,
  Input,
  Tag,
  Empty,
} from "antd";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaPlus,
  FaUsers,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import { Get } from "../../config/api/get";
import { Post } from "../../config/api/post";
import { Patch } from "../../config/api/patch";
import { Delete } from "../../config/api/delete";
import { ASSOCIATE } from "../../config/constants/api";
import { CONTENT_TYPE } from "../../config/constants/index";
import { ImageUrl } from "../../helpers/Functions";
import "../course-management/course-management.css";

const STATUS_COLORS = {
  PENDING: "gold",
  ACTIVE: "green",
  INACTIVE: "red",
};

function AssociateManagement() {
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const user = useSelector((state) => state.user.userData);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const canManage = Boolean(user?.hasTrainingAssociates);

  useEffect(() => {
    if (!token) {
      navigate("/signin");
      return;
    }
    if (!canManage) {
      return;
    }
    loadAssociates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, canManage]);

  const loadAssociates = async () => {
    setLoading(true);
    try {
      const response = await Get(ASSOCIATE.list, token);
      if (response?.status) {
        setAssociates(response?.data?.associates || []);
      } else {
        swal("Oops!", response?.message || "Failed to load associates", "error");
      }
    } catch (error) {
      swal("Oops!", error?.message || "Failed to load associates", "error");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditMode(false);
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditMode(true);
    setEditingId(record._id);
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      email: record.email,
      position: record.position,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditMode(false);
    setEditingId(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (editMode && editingId) {
        const response = await Patch(
          ASSOCIATE.update + editingId,
          token,
          values
        );
        if (response?.status) {
          swal("Success!", response?.message || "Associate updated", "success");
          closeModal();
          loadAssociates();
        } else {
          swal("Oops!", response?.message || "Update failed", "error");
        }
      } else {
        const response = await Post(
          ASSOCIATE.create,
          values,
          token,
          null,
          CONTENT_TYPE.JSON
        );
        const payload = response?.data ?? response;
        if (payload?.status) {
          swal(
            "Success!",
            payload?.message ||
              "Associate added and sent for admin approval.",
            "success"
          );
          closeModal();
          loadAssociates();
        } else {
          swal("Oops!", payload?.message || "Create failed", "error");
        }
      }
    } catch (error) {
      swal("Oops!", error?.message || "Request failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    swal({
      title: "Remove associate?",
      text: `${record.firstName} ${record.lastName} will be removed or deactivated.`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      try {
        const response = await Delete(ASSOCIATE.delete + record._id, token);
        if (response?.status) {
          swal("Success!", response?.message || "Associate removed", "success");
          loadAssociates();
        } else {
          swal("Oops!", response?.message || "Delete failed", "error");
        }
      } catch (error) {
        swal("Oops!", error?.message || "Delete failed", "error");
      }
    });
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 80,
      render: (_, __, index) => (
        <span className="cm-index">
          {(index < 9 ? "0" : "") + (index + 1)}
        </span>
      ),
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <span className="cm-title-cell">
          {record.firstName} {record.lastName}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => <span className="cm-category-cell">{email}</span>,
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      render: (position) => (
        <span className="cm-meta-cell">{position}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="cm-view-btn"
            aria-label="View & assign courses"
            title="View & assign courses"
            onClick={() => navigate(`/associate-management/${record._id}`)}
          >
            <FaEye aria-hidden="true" />
          </button>
          <button
            type="button"
            className="cm-view-btn"
            aria-label="Edit associate"
            title="Edit"
            onClick={() => openEditModal(record)}
          >
            <FaEdit aria-hidden="true" />
          </button>
          <button
            type="button"
            className="cm-view-btn"
            aria-label="Delete associate"
            title="Delete"
            onClick={() => handleDelete(record)}
            style={{ color: "#c62828" }}
          >
            <FaTrash aria-hidden="true" />
          </button>
        </div>
      ),
    },
  ];

  const heroSection = (
    <section
      className="cm-hero"
      style={{
        backgroundImage: `url(${ImageUrl("coursesbnr.png")})`,
      }}
    >
      <div className="cm-hero-overlay" aria-hidden />
      <div className="cm-wrap cm-hero-inner">
        <div className="cm-hero-copy">
          <span className="cm-hero-badge">ASSOCIATES</span>
          <h1 className="fontFamily1 cm-hero-title">
            Manage Associates. <span>Empower Teams.</span>
          </h1>
          <p className="fontFamily1 cm-hero-sub">
            Add and manage training associates to support your course delivery.
          </p>
          {canManage && (
            <div className="cm-hero-actions">
              <Button
                type="primary"
                size="large"
                className="cm-hero-btn-primary"
                onClick={openAddModal}
              >
                <span className="cm-hero-btn-plus">
                  <FaPlus />
                </span>
                Add associate
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );

  if (!canManage) {
    return (
      <Layout className="cm-page">
        {heroSection}
        <div className="cm-wrap cm-main">
          <div className="cm-panel">
            <div className="cm-toolbar">
              <div className="cm-toolbar-intro">
                <span className="cm-toolbar-icon" aria-hidden>
                  <FaUsers />
                </span>
                <h2 className="fontFamily1">Training associates</h2>
              </div>
            </div>
            <div style={{ padding: "28px 24px" }}>
              <Typography.Paragraph>
                Training associates are not enabled on your account. Contact
                admin if you need this feature.
              </Typography.Paragraph>
              <Button
                type="primary"
                className="cm-btn-course"
                onClick={() => navigate("/dashboard")}
              >
                Back to dashboard
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout className="cm-page">
      {heroSection}

      <div className="cm-wrap cm-main">
        <div className="cm-panel">
          <div className="cm-toolbar">
            <div className="cm-toolbar-intro">
              <span className="cm-toolbar-icon" aria-hidden>
                <FaUsers />
              </span>
              <h2 className="fontFamily1">Training associates</h2>
            </div>
            <div className="cm-toolbar-actions">
              <Button
                type="primary"
                className="cm-btn-course"
                onClick={openAddModal}
              >
                Add associate
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="cm-skeleton">
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>
          ) : associates.length === 0 ? (
            <div style={{ padding: "28px 24px" }}>
              <Empty description="No associates yet" />
            </div>
          ) : (
            <div className="cm-table-wrap">
              <Table
                rowKey="_id"
                columns={columns}
                dataSource={associates}
                pagination={false}
                className="cm-table"
                scroll={{ x: "max-content" }}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        title={editMode ? "Edit associate" : "Add associate"}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="First name"
            name="firstName"
            rules={[{ required: true, message: "First name is required" }]}
          >
            <Input size="large" placeholder="First name" />
          </Form.Item>
          <Form.Item
            label="Last name"
            name="lastName"
            rules={[{ required: true, message: "Last name is required" }]}
          >
            <Input size="large" placeholder="Last name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input size="large" placeholder="Email" />
          </Form.Item>
          <Form.Item
            label="Position"
            name="position"
            rules={[{ required: true, message: "Position is required" }]}
          >
            <Input size="large" placeholder="e.g. Training coordinator" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button onClick={closeModal} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {editMode ? "Save changes" : "Add associate"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default AssociateManagement;
